import styled from "@emotion/styled"
import { Container, Grid, Typography } from "@mui/material"
import { GlobalContext } from "common/GlobalContext"
import SeoMetaData from "common/SeoMetaData"
import {
  StaticPropsFactory,
  StaticPropsFactoryProps,
} from "common/StaticPropsFactory"
import CustomerServiceIcon from "common/icons/ContactUs/CustomerServiceIcon"
import EmailIcon from "common/icons/ContactUs/EmailIcon"
import WorkOrderIcon from "common/icons/ContactUs/WorkOrderIcon"
import { formatPhone } from "common/utils/formatters"
import ContactUsForm from "components/ContactUs/ContactUsForm"
import { useContext } from "react"

const Styled = {
  Wrapper: styled.div`
    padding: 21px 0;
    text-align: left; /* flipped by mui */

    ${(p) => p.theme.breakpoints.up("md")} {
      padding: 100px 0;
    }
  `,
  Title: styled(Typography)`
    ${(p) => p.theme.breakpoints.up("md")} {
      line-height: 1.25;
    }
    text-align: left; /* flipped by mui */
  `,
  Subtitle: styled(Typography)`
    font-size: 18px;
    color: ${(p) => p.theme.colors.bluishGrey};
    text-align: left; /* flipped by mui */
    ${(p) => p.theme.breakpoints.up("md")} {
      margin-top: 10px;
      font-size: 20px;
      line-height: 28px;
    }
  `,
  Sections: styled.div`
    margin: 32px 0;
  `,
  Section: styled.div`
    display: flex;
    align-items: flex-start;
    gap: 18px;

    &:not(:first-of-type) {
      margin-top: 22px;
      ${(p) => p.theme.breakpoints.up("md")} {
        margin-top: 24px;
      }
    }
  `,
  Icon: styled.div`
    width: 50px;
    text-align: center;
    svg {
      width: auto;
    }
    .svg-customer {
      height: 27px;
    }
    .svg-email {
      height: 17px;
    }
    .svg-workorder {
      height: 20px;
    }
  `,
  Content: styled.div`
    font-size: 16px;
    text-align: left; /* flipped by mui */

    ${(p) => p.theme.breakpoints.up("md")} {
      font-size: 18px;
    }

    color: ${(p) => p.theme.colors.marine};

    .contact:not(:last-of-type) {
      margin-bottom: 20px;
    }

    .title {
      font-weight: bold;
      margin-bottom: 6px;
      ${(p) => p.theme.breakpoints.up("md")} {
        margin-bottom: 9px;
      }
    }
    .area {
      text-decoration: underline;
    }
  `,
  Grid: styled(Grid)`
    ${(p) => p.theme.breakpoints.down("sm")} {
      & > div:not(:last-of-type) {
        border-bottom: solid 1.5px ${(p) => p.theme.colors.silver};
      }
    }

    ${(p) => p.theme.breakpoints.up("md")} {
      & > div:not(:last-of-type) {
        border-inline-end: solid 1.5px ${(p) => p.theme.colors.silver};
      }
    }
  `,
  LeftGrid: styled(Grid)`
    ${(p) => p.theme.breakpoints.up("md")} {
      padding-inline-end: 77.5px;
    }
  `,
  RightGrid: styled(Grid)`
    ${(p) => p.theme.breakpoints.up("md")} {
      padding-inline-start: 77.5px;
    }
  `,
}

export const getStaticProps = StaticPropsFactory("contactUs")

const ContactUs = ({ seo }: StaticPropsFactoryProps) => {
  const { contacts } = useContext(GlobalContext)
  return (
    <Styled.Wrapper>
      <SeoMetaData data={seo} />
      <Container>
        <Styled.Grid container>
          <Styled.LeftGrid item xs={12} sm={12} md={4}>
            <Styled.Title variant="h1">
              נא מלא את הפרטים ליצירת קשר
            </Styled.Title>
            <Styled.Subtitle>
              נא מלא את הפרטים שלך בטופס הפנייה ונציג יחזור אליך בהקדם האפשרי
            </Styled.Subtitle>
            <Styled.Sections>
              <Styled.Section>
                <Styled.Icon>
                  <CustomerServiceIcon className="svg-customer" />
                </Styled.Icon>
                <Styled.Content>
                  <div className="title">מוקד שירות לקוחות</div>
                  <a href="tel:08-9139995">08-9139995</a>
                </Styled.Content>
              </Styled.Section>
              <Styled.Section>
                <Styled.Icon>
                  <EmailIcon className="svg-email" />
                </Styled.Icon>
                <Styled.Content>
                  <div className="title">דוא״ל שירות לקוחות</div>
                  <a href="mailto:service1@delekmotors.co.il">
                    service1@delekmotors.co.il
                  </a>
                </Styled.Content>
              </Styled.Section>
              <Styled.Section>
                <Styled.Icon>
                  <WorkOrderIcon className="svg-workorder" />
                </Styled.Icon>
                <Styled.Content>
                  <div className="title">נציגי שירות</div>
                  {contacts.map((saleContact, i) => (
                    <div className="contact" key={i}>
                      <div className="area">{saleContact.area}</div>
                      <div>
                        {saleContact.name} -{" "}
                        <a href={`tel:${formatPhone(saleContact.phoneNumber)}`}>
                          {formatPhone(saleContact.phoneNumber)}
                        </a>
                      </div>
                    </div>
                  ))}
                </Styled.Content>
              </Styled.Section>
            </Styled.Sections>
          </Styled.LeftGrid>
          <Styled.RightGrid item xs={12} sm={12} md={8}>
            <ContactUsForm />
          </Styled.RightGrid>
        </Styled.Grid>
      </Container>
    </Styled.Wrapper>
  )
}

export default ContactUs
